import React, { useState, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  ExpandedState,
  Row,
  Header,
  HeaderGroup,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Popover,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  TableSortLabel,
  TextField,
  Button,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { List as VirtualList, type RowComponentProps } from "react-window";

// Filter options type for server-side filtering
export interface FilterOptions {
  [columnId: string]: string[];
}

// Active filters type
export interface ActiveFilters {
  [columnId: string]: string[];
}

// Sort state type - null means no sort applied
export interface SortState {
  column: string;
  direction: "asc" | "desc";
}

export interface TanStackDataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  loading?: boolean;
  title?: string;
  maxHeight?: string;
  getSubRows?: (row: TData) => TData[] | undefined;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  expandableColumnId?: string;
  grandTotalField?: string;
  grandTotalValue?: string;
  defaultExpanded?: boolean | ExpandedState;
  depthIndent?: number;
  rowDepthColors?: string[];
  // Server-side filtering props
  enableFilters?: boolean;
  filterableColumns?: string[]; // If provided, only these columns will have filters. If empty/undefined with enableFilters=true, all columns with filterOptions will have filters
  filterOptions?: FilterOptions;
  activeFilters?: ActiveFilters;
  onFilterChange?: (filters: ActiveFilters) => void;
  // Server-side sorting props
  enableSorting?: boolean;
  sortableColumns?: string[]; // If provided, only these columns will be sortable. If empty/undefined with enableSorting=true, all columns will be sortable
  sortState?: SortState | null; // null means no sort applied
  onSortChange?: (sort: SortState | null) => void; // null to clear sort
  // Virtualization props
  enableVirtualization?: boolean;
  rowHeight?: number; // Height of each row in pixels for virtualization
  // Pin first column
  pinFirstColumn?: boolean;
}

// Helper to generate depth-based background colors
const getDepthColor = (depth: number, colors?: string[]): string => {
  const defaultColors = [
    "#ffffff", // depth 0 - white
    "#ffffff", // depth 1 - white
    "#ffffff", // depth 2 - white
    "#ffffff", // depth 3+ - white
  ];
  const colorPalette = colors || defaultColors;
  return colorPalette[Math.min(depth, colorPalette.length - 1)];
};

// Helper to get font weight based on depth
const getDepthFontWeight = (depth: number, canExpand: boolean): number => {
  if (depth === 0) return 700; // Root level - bold
  if (canExpand) return 600; // Has children - semi-bold
  return 400; // Leaf node - normal
};

export function TanStackDataTable<TData>({
  data,
  columns,
  loading = false,
  title,
  maxHeight = "600px",
  getSubRows,
  expandableColumnId = "category",
  grandTotalField = "category",
  grandTotalValue = "Grand Total",
  defaultExpanded = false,
  depthIndent = 4,
  rowDepthColors,
  enableFilters = false,
  filterableColumns,
  filterOptions = {},
  activeFilters = {},
  onFilterChange,
  enableSorting = false,
  sortableColumns,
  sortState,
  onSortChange,
  enableVirtualization = false,
  rowHeight = 32,
  pinFirstColumn = false,
}: TanStackDataTableProps<TData>) {
  // Initialize expanded state - can be boolean (all expanded/collapsed) or specific state
  const initialExpanded = useMemo<ExpandedState>(() => {
    if (typeof defaultExpanded === "boolean") {
      return defaultExpanded ? true : {};
    }
    return defaultExpanded;
  }, [defaultExpanded]);

  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null,
  );
  // Local state for pending filter selections (before Apply)
  const [pendingFilterSelections, setPendingFilterSelections] = useState<
    string[]
  >([]);
  // Search state for filter values
  const [filterSearchText, setFilterSearchText] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      columnFilters,
      sorting,
    },
    onExpandedChange: setExpanded,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Calculate total leaf columns for colSpan
  const leafColumnCount = useMemo(() => {
    return table.getAllLeafColumns().length;
  }, [table]);

  // Check if a row is the grand total row
  const isGrandTotalRow = (row: Row<TData>): boolean => {
    const original = row.original as Record<string, any>;
    return original[grandTotalField] === grandTotalValue;
  };

  // Ref for the scrollable table container (for virtualization)
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Get rows excluding grand total for virtualization
  const { rows } = table.getRowModel();
  const dataRows = useMemo(
    () => rows.filter((row) => !isGrandTotalRow(row)),
    [rows, grandTotalField, grandTotalValue],
  );
  const grandTotalRows = useMemo(
    () => rows.filter((row) => isGrandTotalRow(row)),
    [rows, grandTotalField, grandTotalValue],
  );

  // Row virtualizer - only used when enableVirtualization is true
  const rowVirtualizer = useVirtualizer({
    count: enableVirtualization ? dataRows.length : 0,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    enabled: enableVirtualization,
  });

  // Column virtualizer - only used when enableVirtualization is true
  const visibleColumns = table.getVisibleLeafColumns();
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: enableVirtualization ? visibleColumns.length : 0,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: (index) => visibleColumns[index]?.getSize() ?? 100,
    overscan: 3,
    enabled: enableVirtualization,
  });

  // Handle filter popover open
  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
  ) => {
    event.stopPropagation();
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnId);
    // Initialize pending selections with current active filters for this column
    setPendingFilterSelections(activeFilters[columnId] || []);
    setFilterSearchText("");
  };

  // Handle filter popover close (without applying)
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
    setPendingFilterSelections([]);
    setFilterSearchText("");
  };

  // Handle filter value toggle (local state only, not applied yet)
  const handleFilterToggle = (value: string) => {
    setPendingFilterSelections((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  // Apply filter selections
  const handleFilterApply = () => {
    if (!activeFilterColumn) return;

    const newFilters = { ...activeFilters };
    if (pendingFilterSelections.length === 0) {
      delete newFilters[activeFilterColumn];
    } else {
      newFilters[activeFilterColumn] = pendingFilterSelections;
    }

    onFilterChange?.(newFilters);
    handleFilterClose();
  };

  // Clear filter selections
  const handleFilterClear = () => {
    setPendingFilterSelections([]);
  };

  // Handle sort click - three-way toggle: asc -> desc -> none (null)
  const handleSortClick = (columnId: string) => {
    if (!enableSorting) return;

    // If onSortChange is provided, use server-side sorting (controlled)
    if (onSortChange) {
      if (sortState?.column === columnId) {
        // Same column clicked - cycle through: asc -> desc -> none
        if (sortState.direction === "asc") {
          onSortChange({ column: columnId, direction: "desc" });
        } else {
          // Was desc, now clear sort
          onSortChange(null);
        }
      } else {
        // Different column clicked - start with asc
        onSortChange({ column: columnId, direction: "asc" });
      }
    } else {
      // Use internal client-side sorting (uncontrolled)
      const currentSort = sorting.find((s) => s.id === columnId);
      if (currentSort) {
        if (currentSort.desc === false) {
          // Was asc, now desc
          setSorting([{ id: columnId, desc: true }]);
        } else {
          // Was desc, now clear
          setSorting([]);
        }
      } else {
        // Start with asc
        setSorting([{ id: columnId, desc: false }]);
      }
    }
  };

  // Render header cell with support for grouped columns, filters, and sorting
  const renderHeaderCell = (
    header: Header<TData, unknown>,
    isFirstColumn: boolean,
  ) => {
    const isGroupHeader = header.column.columns.length > 0;
    const columnId = header.column.id;

    // Check if this column should have filter (either in filterableColumns list, or all columns if list not provided)
    const isFilterableColumn = filterableColumns
      ? filterableColumns.includes(columnId)
      : true;
    const hasFilterOptions =
      enableFilters &&
      isFilterableColumn &&
      filterOptions[columnId]?.length > 0;
    const activeFilterCount = activeFilters[columnId]?.length || 0;

    // Check if this column should be sortable (either in sortableColumns list, or all columns if list not provided)
    const isSortableColumn = sortableColumns
      ? sortableColumns.includes(columnId)
      : true;
    const canSort = enableSorting && isSortableColumn && !isGroupHeader;

    // Check sort state - use internal sorting state if sortState prop not provided
    const internalSort = sorting.find((s) => s.id === columnId);
    const isSorted = sortState ? sortState.column === columnId : !!internalSort;
    const sortDirection = sortState
      ? sortState.direction || "asc"
      : internalSort?.desc
        ? "desc"
        : "asc";

    return (
      <TableCell
        key={header.id}
        colSpan={header.colSpan}
        sx={{
          fontWeight: 600,
          // bgcolor: isGroupHeader ? "#d0d9e3" : "#e9ecef",
          fontSize: "0.7rem",
          borderBottom: "2px solid #e2e8f0",
          borderRight: isGroupHeader ? "1px solid #c0c9d3" : undefined,
          color: "#475569",
          py: 0.25,
          px: 0.5,
          textAlign: isGroupHeader
            ? "center"
            : isFirstColumn
              ? "left"
              : "right",
          minWidth: header.column.columnDef.minSize,
          maxWidth: header.column.columnDef.maxSize,
          width: header.column.columnDef.size,
          verticalAlign: "middle",
          ...(pinFirstColumn &&
            isFirstColumn && {
              position: "sticky",
              left: 0,
              zIndex: 3,
              boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
            }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isFirstColumn ? "flex-start" : "flex-end",
            gap: 0,
          }}
        >
          {/* Column Header with Sort */}
          {canSort ? (
            <TableSortLabel
              active={isSorted}
              direction={isSorted ? sortDirection : "asc"}
              onClick={() => handleSortClick(columnId)}
              sx={{
                fontSize: "0.72rem",
                "& .MuiTableSortLabel-icon": { fontSize: "0.9rem" },
              }}
            >
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
            </TableSortLabel>
          ) : header.isPlaceholder ? null : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}

          {/* Filter Icon */}
          {hasFilterOptions && (
            <Tooltip title={`Filter ${columnId}`}>
              <IconButton
                size="small"
                onClick={(e) => handleFilterClick(e, columnId)}
                sx={{ p: 0, ml: -0.25, flexShrink: 0 }}
              >
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.6rem",
                      height: 14,
                      minWidth: 14,
                    },
                  }}
                >
                  <FilterIcon
                    sx={{
                      fontSize: "0.85rem",
                      color: activeFilterCount > 0 ? "#1976d2" : "#94a3b8",
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    );
  };

  // Filter popover content with search and Apply/Clear buttons
  const filterPopoverContent =
    activeFilterColumn &&
    filterOptions[activeFilterColumn] &&
    (() => {
      // Filter options based on search text
      const filteredOptions = filterOptions[activeFilterColumn].filter(
        (value) =>
          (value || "").toLowerCase().includes(filterSearchText.toLowerCase()),
      );

      // Select All handler - selects all filtered options
      const handleSelectAll = () => {
        setPendingFilterSelections([...filteredOptions]);
      };

      // Clear All handler - clears all selections
      const handleClearAll = () => {
        setPendingFilterSelections([]);
      };

      return (
        <Box sx={{ width: 220, maxWidth: 220 }}>
          {/* Search Input */}
          <Box sx={{ p: 0.75 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={filterSearchText}
              onChange={(e) => setFilterSearchText(e.target.value)}
              fullWidth
              sx={{
                "& .MuiInputBase-input": { fontSize: "0.7rem", py: 0.5 },
              }}
            />
          </Box>

          {/* Select All / Clear All */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 0.75,
              py: 0.5,
            }}
          >
            <Button
              size="small"
              onClick={handleSelectAll}
              sx={{
                fontSize: "0.65rem",
                textTransform: "none",
                p: 0.25,
                minWidth: "auto",
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={handleClearAll}
              sx={{
                fontSize: "0.65rem",
                textTransform: "none",
                p: 0.25,
                minWidth: "auto",
              }}
            >
              Clear All
            </Button>
          </Box>
          <Divider />

          {/* Options List - Virtualized */}
          {filteredOptions.length === 0 ? (
            <Box sx={{ p: 1 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                No matches found
              </Typography>
            </Box>
          ) : (
            <VirtualList
              rowComponent={({
                index,
                style,
                options,
                selected,
                onToggle,
              }: RowComponentProps<{
                options: string[];
                selected: string[];
                onToggle: (v: string) => void;
              }>) => {
                const value = options[index];
                const isSelected = selected.includes(value);
                return (
                  <ListItemButton
                    style={style}
                    onClick={() => onToggle(value)}
                    dense
                    sx={{ py: 0, minHeight: 28 }}
                  >
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        sx={{ p: 0.25 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={value || "(empty)"}
                      primaryTypographyProps={{
                        fontSize: "0.7rem",
                        noWrap: true,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </ListItemButton>
                );
              }}
              rowCount={filteredOptions.length}
              rowHeight={28}
              rowProps={{
                options: filteredOptions,
                selected: pendingFilterSelections,
                onToggle: handleFilterToggle,
              }}
              style={{ height: 200 }}
            />
          )}
          <Divider />

          {/* Clear and Apply Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 0.75,
              gap: 0.5,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={handleFilterClear}
              sx={{ flex: 1, fontSize: "0.7rem", textTransform: "none" }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleFilterApply}
              sx={{ flex: 1, fontSize: "0.7rem", textTransform: "none" }}
            >
              Apply ({pendingFilterSelections.length})
            </Button>
          </Box>
        </Box>
      );
    })();

  // Render header groups (supports multiple header rows for grouped columns)
  const renderHeaderGroups = (headerGroups: HeaderGroup<TData>[]) => {
    return headerGroups.map((headerGroup) => (
      <TableRow key={headerGroup.id}>
        {headerGroup.headers.map((header, index) =>
          renderHeaderCell(header, index === 0),
        )}
      </TableRow>
    ));
  };

  return (
    <Box>
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1.125rem",
            color: "#1e293b",
            mb: 2,
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ position: "relative" }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                boxShadow: 3,
                px: 4,
                py: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontWeight: 500 }}
              >
                Loading...
              </Typography>
            </Box>
          </Box>
        )}

        {enableVirtualization ? (
          /* Virtualized Table Rendering */
          <TableContainer
            ref={tableContainerRef}
            component={Paper}
            sx={{
              maxHeight,
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              overflow: "auto",
            }}
          >
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                {renderHeaderGroups(table.getHeaderGroups())}
              </TableHead>
              <TableBody>
                {dataRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={leafColumnCount}
                      sx={{ textAlign: "center", py: 8 }}
                    >
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {/* Spacer for virtualization */}
                    <TableRow
                      style={{
                        height: `${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px`,
                      }}
                    >
                      <TableCell
                        colSpan={leafColumnCount}
                        sx={{ p: 0, border: 0 }}
                      />
                    </TableRow>
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const row = dataRows[virtualRow.index];
                      if (!row) return null;

                      const canExpand = row.getCanExpand();
                      const depth = row.depth;
                      const fontWeight = getDepthFontWeight(depth, canExpand);
                      const bgColor = canExpand
                        ? getDepthColor(depth, rowDepthColors)
                        : "white";
                      const hoverColor = "#f9f9f9";

                      return (
                        <TableRow
                          key={row.id}
                          data-index={virtualRow.index}
                          sx={{
                            bgcolor: bgColor,
                            height: rowHeight,
                            "&:hover": { bgcolor: hoverColor },
                          }}
                        >
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isExpandableColumn =
                              cell.column.id === expandableColumnId;
                            const isFirstColumn = cellIndex === 0;
                            const paddingLeft = isExpandableColumn
                              ? 2 + depth * depthIndent
                              : undefined;

                            return (
                              <TableCell
                                key={cell.id}
                                sx={{
                                  py: 0.25,
                                  px: 0.5,
                                  fontSize: "0.7rem",
                                  fontWeight: fontWeight,
                                  pl: paddingLeft,
                                  textAlign: isFirstColumn ? "left" : "right",
                                  whiteSpace: "normal",
                                  wordBreak: isFirstColumn
                                    ? "break-word"
                                    : "normal",
                                  ...(pinFirstColumn &&
                                    isFirstColumn && {
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 1,
                                      bgcolor: bgColor,
                                      boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                                    }),
                                }}
                              >
                                {isExpandableColumn && canExpand ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={row.getToggleExpandedHandler()}
                                      sx={{ mr: 1, p: 0 }}
                                    >
                                      {row.getIsExpanded() ? (
                                        <ExpandIcon fontSize="small" />
                                      ) : (
                                        <CollapseIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </Box>
                                ) : (
                                  flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                    {/* Bottom spacer for virtualization */}
                    <TableRow
                      style={{
                        height: `${rowVirtualizer.getTotalSize() - (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.end ?? 0)}px`,
                      }}
                    >
                      <TableCell
                        colSpan={leafColumnCount}
                        sx={{ p: 0, border: 0 }}
                      />
                    </TableRow>
                  </>
                )}
              </TableBody>
              {/* Pinned Grand Total Row at Bottom */}
              {grandTotalRows.length > 0 && (
                <TableBody sx={{ position: "sticky", bottom: 0, zIndex: 1 }}>
                  {grandTotalRows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        bgcolor: "white",
                        "&:hover": { bgcolor: "#f9f9f9" },
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <TableCell
                          key={cell.id}
                          sx={{
                            py: 0.25,
                            px: 0.5,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#333",
                            textAlign: cellIndex === 0 ? "left" : "right",
                            borderBottom: 0,
                            ...(pinFirstColumn &&
                              cellIndex === 0 && {
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                bgcolor: "white",
                                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                              }),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        ) : (
          /* Non-virtualized Table Rendering (original) */
          <TableContainer
            component={Paper}
            sx={{
              maxHeight,
              border: "1px solid #e2e8f0",
              borderRadius: 2,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                {renderHeaderGroups(table.getHeaderGroups())}
              </TableHead>
              <TableBody>
                {dataRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={leafColumnCount}
                      sx={{ textAlign: "center", py: 8 }}
                    >
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataRows.map((row) => {
                    const canExpand = row.getCanExpand();
                    const depth = row.depth;
                    const fontWeight = getDepthFontWeight(depth, canExpand);
                    const bgColor = canExpand
                      ? getDepthColor(depth, rowDepthColors)
                      : "white";
                    const hoverColor = "#f5f5f5";

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          bgcolor: bgColor,
                          "&:hover": { bgcolor: hoverColor },
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isExpandableColumn =
                            cell.column.id === expandableColumnId;
                          const isFirstColumn = cellIndex === 0;
                          const paddingLeft = isExpandableColumn
                            ? 2 + depth * depthIndent
                            : undefined;

                          return (
                            <TableCell
                              key={cell.id}
                              sx={{
                                py: 0.25,
                                px: 0.5,
                                fontSize: "0.7rem",
                                fontWeight: fontWeight,
                                pl: paddingLeft,
                                textAlign: isFirstColumn ? "left" : "right",
                                ...(pinFirstColumn &&
                                  isFirstColumn && {
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 1,
                                    bgcolor: bgColor,
                                    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                                  }),
                              }}
                            >
                              {isExpandableColumn && canExpand ? (
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={row.getToggleExpandedHandler()}
                                    sx={{ mr: 1, p: 0 }}
                                  >
                                    {row.getIsExpanded() ? (
                                      <ExpandIcon fontSize="small" />
                                    ) : (
                                      <CollapseIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </Box>
                              ) : (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {/* Pinned Grand Total Row at Bottom */}
              {grandTotalRows.length > 0 && (
                <TableBody sx={{ position: "sticky", bottom: 0, zIndex: 1 }}>
                  {grandTotalRows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        bgcolor: "white",
                        "&:hover": { bgcolor: "#f9f9f9" },
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <TableCell
                          key={cell.id}
                          sx={{
                            py: 0.25,
                            px: 0.5,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#333",
                            textAlign: cellIndex === 0 ? "left" : "right",
                            borderBottom: 0,
                            ...(pinFirstColumn &&
                              cellIndex === 0 && {
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                bgcolor: "white",
                                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                              }),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {filterPopoverContent}
      </Popover>
    </Box>
  );
}

export default TanStackDataTable;
